import type { EChartsOption } from 'echarts'
import { axisTextStyle, richSafe, tooltipBase, tooltipHtml } from './format'
import { rampColor, type ChartTheme } from './palette'

export interface GanttPhase {
  key: string
  label: string
}

export interface GanttSegment {
  /** Ключ фазы из `phases`. */
  phase: string
  /** `YYYY-MM-DD`. */
  start: string
  end: string
  /** Пояснение в подсказке. */
  note?: string
}

export interface GanttRow {
  key: string
  label: string
  segments: GanttSegment[]
}

export interface GanttInput {
  rows: GanttRow[]
  /** Фазы по порядку (порядковая шкала одного оттенка): «Запустить до» → «В ассортименте» → «Пик». */
  phases: GanttPhase[]
  /** Отметка «сегодня» (`YYYY-MM-DD`); по умолчанию не рисуется. */
  today?: string | null
  /** Границы оси; по умолчанию — от первого до последнего отрезка. */
  from?: string
  to?: string
  /**
   * Вертикальные отметки событий (праздники сезона): тонкая линия с короткой подписью сверху, над строкой
   * месяцев оси, и пояснением в подсказке. Без отметок график строится как прежде.
   */
  markers?: GanttMarker[]
  /** Ширина колонки подписей строк, px (по умолчанию 160). */
  labelWidth?: number
}

export interface GanttMarker {
  /** `YYYY-MM-DD`. */
  date: string
  label: string
  /** Пояснение в подсказке (например, «в ассортименте до 01.11»). */
  note?: string
}

export const DAY_MS = 24 * 60 * 60 * 1000

/** `YYYY-MM-DD` → метка времени полуночи по местному времени. */
export function isoToTime(iso: string): number {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, (month || 1) - 1, day || 1).getTime()
}

export function formatShortDate(time: number): string {
  const date = new Date(time)
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`
}

/** Данные custom-серии: [строка, начало, конец (включительно, до конца дня), номер фазы, номер отрезка]. */
export function ganttData(input: GanttInput): Array<[number, number, number, number, number]> {
  const phaseIndex = new Map(input.phases.map((phase, index) => [phase.key, index]))
  const data: Array<[number, number, number, number, number]> = []
  input.rows.forEach((row, rowIndex) => {
    row.segments.forEach((segment, segmentIndex) => {
      const phase = phaseIndex.get(segment.phase)
      if (phase === undefined) return
      const start = isoToTime(segment.start)
      const end = isoToTime(segment.end) + DAY_MS
      if (!(end > start)) return
      data.push([rowIndex, start, end, phase, segmentIndex])
    })
  })
  return data
}

export function phaseColor(theme: ChartTheme, phase: number, count: number): string {
  // Ранняя фаза — светлее, пик — насыщеннее (в тёмной теме наоборот: порядковая шкала уже перевёрнута).
  return rampColor(theme.ordinal, count > 1 ? 1 - phase / (count - 1) : 0)
}

/** Сколько ярусов подписей отметок над осью месяцев; не влезло — подпись только в подсказке. */
export const MARKER_LABEL_TIERS = 3
/** Ширина подписи отметки в долях окна таймлайна (≈ 80 px на типичной ширине карточки). */
export const MARKER_LABEL_SPAN = 0.09
export const MARKER_TIER_STEP = 14
/**
 * Высота строки подписей месяцев оси времени (ось — сверху). Подписи праздников стоят у верхнего конца
 * вертикали (ось строк перевёрнута, «начало» отметки — сверху) и поднимаются над месяцами на эту высоту,
 * иначе «Ноя», «Мар» налезали на названия праздников.
 */
export const MONTH_LABEL_CLEARANCE = 18
/** Отступ подписи праздника на ярусе `tier` от верха области графика. */
export const markerLabelDistance = (tier: number): number => 4 + MONTH_LABEL_CLEARANCE + tier * MARKER_TIER_STEP

/**
 * Ярус подписи для каждой отметки (Ф6 аудита 20.10: подписи праздников налезали друг на друга). Отметки по
 * времени раскладываются жадно: подпись встаёт на нижний свободный ярус, где до предыдущей подписи не меньше
 * `minGap`; свободного яруса нет — `null`, подпись видна только в подсказке.
 */
export function markerLabelTiers(times: number[], minGap: number, tiers = MARKER_LABEL_TIERS): Array<number | null> {
  const order = times.map((time, index) => ({ time, index })).sort((a, b) => a.time - b.time)
  const last: Array<number | null> = Array.from({ length: tiers }, () => null)
  const result: Array<number | null> = times.map(() => null)
  for (const { time, index } of order) {
    const tier = last.findIndex((previous) => previous === null || time - previous >= minGap)
    if (tier < 0) continue
    last[tier] = time
    result[index] = tier
  }
  return result
}

export function ganttHeight(rows: number): number {
  return Math.max(120, rows * 32 + 40)
}

/**
 * Сезонный обратный отсчёт: строки — темы, по горизонтали — время, отрезки — фазы (порядковая шкала;
 * легенда фаз — HTML-компонентом `ChartLegend` над графиком, у custom-серии своей легенды нет).
 * Отметка «сегодня» — вертикальная линия. Рисуется custom-серией: прямоугольник от начала до конца фазы
 * высотой 60 % строки, скругление 4 px.
 */
export function buildGanttOption(input: GanttInput, theme: ChartTheme): EChartsOption {
  const data = ganttData(input)
  const starts = data.map((item) => item[1])
  const ends = data.map((item) => item[2])
  const min = input.from ? isoToTime(input.from) : starts.length ? Math.min(...starts) : Date.now()
  const max = input.to ? isoToTime(input.to) + DAY_MS : ends.length ? Math.max(...ends) : Date.now() + DAY_MS
  const count = input.phases.length
  const markers = (input.markers ?? []).filter((marker) => {
    const time = isoToTime(marker.date)
    return time >= min && time <= max
  })
  const tiers = markerLabelTiers(
    markers.map((marker) => isoToTime(marker.date)),
    (max - min) * MARKER_LABEL_SPAN,
  )
  const usedTiers = tiers.reduce<number>((top, tier) => (tier === null ? top : Math.max(top, tier + 1)), markers.length > 0 ? 1 : 0)

  return {
    backgroundColor: 'transparent',
    animation: false,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    grid: {
      // Сверху: подписи месяцев (их место учитывает outerBounds) и над ними — ярусы подписей праздников.
      top: markers.length > 0 ? 8 + MONTH_LABEL_CLEARANCE + usedTiers * MARKER_TIER_STEP : 8,
      left: 8,
      right: 16,
      // Снизу — подпись «сегодня» у нижнего конца вертикали.
      bottom: input.today ? 24 : 8,
      outerBoundsMode: 'same',
      outerBoundsContain: 'axisLabel',
    } as any,
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'item',
      formatter: (params: any) => {
        if (params.componentType === 'markLine') {
          const marker = markers.find((item) => item.label === params.name)
          if (!marker) return ''
          return tooltipHtml(theme, formatShortDate(isoToTime(marker.date)), [{ label: '', value: marker.label }], marker.note ? [marker.note] : undefined)
        }
        const [rowIndex, start, end, phase, segmentIndex] = params.value ?? []
        const row = input.rows[rowIndex]
        const segment = row?.segments[segmentIndex]
        if (!row || !segment) return ''
        return tooltipHtml(
          theme,
          row.label,
          [
            {
              color: phaseColor(theme, phase, count),
              label: input.phases[phase]?.label ?? '',
              value: `${formatShortDate(start)} – ${formatShortDate(end - DAY_MS)}`,
            },
          ],
          segment.note ? [segment.note] : undefined,
        )
      },
    },
    xAxis: {
      type: 'time',
      min,
      max,
      position: 'top',
      axisLine: { lineStyle: { color: theme.axis } },
      axisTick: { show: false },
      axisLabel: { ...axisTextStyle(theme), hideOverlap: true },
      splitLine: { show: true, lineStyle: { color: theme.grid, width: 1, type: 'solid' } },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      // Ключи строк, а не подписи: одинаковые названия тем не склеиваются в одну строку; подпись — formatter.
      data: input.rows.map((_row, index) => `r${index}`),
      triggerEvent: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        ...axisTextStyle(theme),
        color: theme.textSecondary,
        fontSize: 12,
        width: input.labelWidth ?? 160,
        overflow: 'truncate',
        interval: 0,
        formatter: (key: string) => input.rows[Number(String(key).slice(1))]?.label ?? '',
      },
    },
    series: [
      {
        type: 'custom',
        cursor: 'pointer',
        encode: { x: [1, 2], y: 0 },
        renderItem: (params: any, api: any) => {
          const row = api.value(0)
          const startPoint = api.coord([api.value(1), row])
          const endPoint = api.coord([api.value(2), row])
          const height = api.size([0, 1])[1] * 0.6
          const rect = {
            x: startPoint[0],
            y: startPoint[1] - height / 2,
            width: Math.max(2, endPoint[0] - startPoint[0]),
            height,
          }
          const coordSys = params.coordSys
          const clipped = {
            x: Math.max(rect.x, coordSys.x),
            y: rect.y,
            width: Math.min(rect.x + rect.width, coordSys.x + coordSys.width) - Math.max(rect.x, coordSys.x),
            height: rect.height,
          }
          if (clipped.width <= 0) return null
          return {
            type: 'rect',
            shape: { ...clipped, r: 4 },
            style: { fill: phaseColor(theme, api.value(3), count) },
          }
        },
        data,
        markLine:
          markers.length === 0
            ? input.today
              ? {
                  silent: true,
                  symbol: 'none',
                  lineStyle: { color: theme.text, width: 1, type: 'solid', opacity: 0.6 },
                  // Снизу, как и при отметках праздников: сверху подпись легла бы на подписи месяцев.
                  label: { formatter: 'сегодня', color: theme.textSecondary, fontSize: 11, fontFamily: theme.fontFamily, position: 'end' },
                  data: [{ xAxis: isoToTime(input.today) }],
                }
              : undefined
            : {
                symbol: 'none',
                label: { color: theme.textSecondary, fontSize: 11, fontFamily: theme.fontFamily },
                emphasis: { disabled: true },
                data: [
                  ...(input.today
                    ? [
                        {
                          xAxis: isoToTime(input.today),
                          name: 'сегодня',
                          silent: true,
                          lineStyle: { color: theme.text, width: 1, type: 'solid', opacity: 0.6 },
                          label: { formatter: 'сегодня', position: 'end' },
                        },
                      ]
                    : []),
                  ...markers.map((marker, index) => {
                    const tier = tiers[index]
                    return {
                      xAxis: isoToTime(marker.date),
                      name: marker.label,
                      lineStyle: { color: theme.textMuted, width: 1, type: 'solid', opacity: 0.7 },
                      label:
                        tier === null
                          ? { show: false }
                          : { formatter: richSafe(marker.label), position: 'start', distance: markerLabelDistance(tier) },
                    }
                  }),
                ],
              },
      },
    ],
  } as EChartsOption
}
